import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Card, Table, Button, Modal, Form, Alert } from "react-bootstrap";
import ReactQuill, { Quill } from "react-quill";
import ImageResize from "quill-image-resize-module-react";
import ImageDropAndPaste from "quill-image-drop-and-paste";
import "react-quill/dist/quill.snow.css";
import {
  applyFloatToSelectedImage,
  contentClipboardMatchers,
  getQuillHtml,
  IMAGE_FLOAT_LEFT_STYLE,
} from "../../utils/quillContentSetup";
import {
  getAllContent,
  updateContent,
  deleteContent,
  uploadContentFile,
  BASE_URL,
} from "../../services/api";
import { getStoredUser } from "../../utils/authStorage";
import "./ContentManagement.css";
import "./Dashboard.css";

Quill.register("modules/imageResize", ImageResize);
Quill.register("modules/imageDropAndPaste", ImageDropAndPaste);

const ContentManagement = () => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentContent, setCurrentContent] = useState({
    id: null,
    jenis: "",
    judul: "",
    isi: "",
    judul_en: "",
    isi_en: "",
  });
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  // Refs for custom toolbar buttons
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const quillIsiRef = useRef(null);
  const quillIsiEnRef = useRef(null);
  const activeQuillRef = useRef(null);
  const imageInsertModeRef = useRef("normal");

  // State & ref for HTML source editor modal
  const [htmlModal, setHtmlModal] = useState({ visible: false, value: "" });
  const htmlSourceQuillRef = useRef(null);

  const uploadAndInsertImage = useCallback(async (file, quill, mode = "normal") => {
    if (!file || !quill) return;

    if (!file.type.startsWith("image/")) {
      setAlert({
        show: true,
        type: "danger",
        message: "Hanya file gambar yang diperbolehkan untuk fitur ini.",
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadContentFile(formData);

      if (!response.data.success) return;

      const url = BASE_URL + response.data.url;
      const range = quill.getSelection(true);
      const insertIndex = range ? range.index : quill.getLength();

      if (mode === "layout") {
        const layoutHtml = `<p class="ql-align-right"><img src="${url}" class="content-img-left" style="${IMAGE_FLOAT_LEFT_STYLE}" />Ketik teks di sini di sebelah kanan foto...</p><p><br></p>`;
        quill.clipboard.dangerouslyPasteHTML(insertIndex, layoutHtml, "user");
        quill.setSelection(insertIndex + 1, 0, "user");
      } else {
        quill.insertEmbed(insertIndex, "image", url, "user");
        quill.setSelection(insertIndex + 1, 0, "user");
      }
    } catch (err) {
      console.error("Image upload error:", err);
      setAlert({
        show: true,
        type: "danger",
        message:
          err.response?.data?.message ||
          "Gagal mengunggah gambar. Pastikan tipe dan ukuran file sesuai.",
      });
    }
  }, []);

  const handleImageSelected = useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      e.target.value = "";

      const quill = activeQuillRef.current;
      const mode = imageInsertModeRef.current;
      imageInsertModeRef.current = "normal";
      await uploadAndInsertImage(file, quill, mode);
    },
    [uploadAndInsertImage],
  );

  // Called when user selects a file from the hidden <input>
  const handleFileSelected = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = ""; // allow re-selecting the same file

    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadContentFile(formData);

      if (response.data.success) {
        const url = BASE_URL + response.data.url;
        const quill = activeQuillRef.current;
        if (quill) {
          const range = quill.getSelection(true);
          const insertIndex = range ? range.index : quill.getLength();
          quill.insertText(insertIndex, file.name, "link", url, "user");
          quill.setSelection(insertIndex + file.name.length, 0, "user");
          quill.format("link", false, "user"); // stop linking on next typed char
        }
      }
    } catch (err) {
      console.error("File upload error:", err);
      setAlert({
        show: true,
        type: "danger",
        message:
          err.response?.data?.message ||
          "Gagal mengunggah file. Pastikan tipe dan ukuran file sesuai (maks 100 MB).",
      });
    }
  }, []);

  // Quill modules configuration
  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["link", "image", "video", "file-upload"],
          ["img-float-left", "img-float-right", "insert-side-layout"],
          ["clean", "code-block", "html-source"],
        ],
        handlers: {
          image: function () {
            activeQuillRef.current = this.quill;
            imageInsertModeRef.current = "normal";
            if (imageInputRef.current) imageInputRef.current.click();
          },
          "file-upload": function () {
            activeQuillRef.current = this.quill;
            if (fileInputRef.current) fileInputRef.current.click();
          },
          "img-float-left": function () {
            const applied = applyFloatToSelectedImage(this.quill, "left");
            if (!applied) {
              setAlert({
                show: true,
                type: "warning",
                message:
                  "Klik di dalam gambar terlebih dahulu, lalu tekan tombol Foto Kiri.",
              });
            }
          },
          "img-float-right": function () {
            const applied = applyFloatToSelectedImage(this.quill, "right");
            if (!applied) {
              setAlert({
                show: true,
                type: "warning",
                message:
                  "Klik di dalam gambar terlebih dahulu, lalu tekan tombol Foto Kanan.",
              });
            }
          },
          "insert-side-layout": function () {
            activeQuillRef.current = this.quill;
            imageInsertModeRef.current = "layout";
            if (imageInputRef.current) imageInputRef.current.click();
          },
          "html-source": function () {
            htmlSourceQuillRef.current = this.quill;
            setHtmlModal({ visible: true, value: this.quill.root.innerHTML });
          },
        },
      },
      imageDropAndPaste: {
        handler: function (_dataUrl, _type, imageData) {
          uploadAndInsertImage(imageData, this.quill, "normal");
        },
      },
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize", "Toolbar"],
      },
      clipboard: {
        matchVisual: true,
        matchers: contentClipboardMatchers,
      },
    }),
    [uploadAndInsertImage],
  );

  const handleHtmlModalClose = useCallback(() => {
    setHtmlModal({ visible: false, value: "" });
    htmlSourceQuillRef.current = null;
  }, []);

  const handleHtmlModalSave = useCallback(() => {
    const quill = htmlSourceQuillRef.current;
    if (quill) {
      quill.clipboard.dangerouslyPasteHTML(htmlModal.value);
    }
    setHtmlModal({ visible: false, value: "" });
    htmlSourceQuillRef.current = null;
  }, [htmlModal.value]);

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "indent",
    "align",
    "link",
    "image",
    "imageFloat",
    "video",
    "code-block",
    "width",
    "height",
    "style",
  ];

  // Helper function to get content type label
  const getContentLabel = (content) => content.judul || content.jenis;

  useEffect(() => {
    fetchContents();
  }, []);

  const fetchContents = async () => {
    try {
      const response = await getAllContent();
      if (response.data.success) {
        setContents(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching contents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (content = null) => {
    if (content) {
      setEditMode(true);
      setCurrentContent({
        ...content,
        judul_en: content.judul_en || "",
        isi_en: content.isi_en || "",
      });
    } else {
      setEditMode(false);
      setCurrentContent({
        id: null,
        jenis: "",
        judul: "",
        isi: "",
        judul_en: "",
        isi_en: "",
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSubmitting(false);
    setCurrentContent({
      id: null,
      jenis: "",
      judul: "",
      isi: "",
      judul_en: "",
      isi_en: "",
    });
    setAlert({ show: false, type: "", message: "" });
  };

  const handleChange = (e) => {
    setCurrentContent({
      ...currentContent,
      [e.target.name]: e.target.value,
    });
  };

  const handleEditorChange = (content) => {
    setCurrentContent((prev) => ({
      ...prev,
      isi: content,
    }));
  };

  const handleEditorChangeEn = (content) => {
    setCurrentContent((prev) => ({
      ...prev,
      isi_en: content,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setAlert({ show: false, type: "", message: "" });

    try {
      const user = getStoredUser();

      const isiHtml =
        getQuillHtml(quillIsiRef.current?.getEditor?.()) || currentContent.isi;
      const isiEnHtml =
        getQuillHtml(quillIsiEnRef.current?.getEditor?.()) ||
        currentContent.isi_en;

      const response = await updateContent({
        jenis: currentContent.jenis,
        judul: currentContent.judul,
        isi: isiHtml,
        userid: user?.userid || "admin",
        judul_en: currentContent.judul_en || null,
        isi_en: isiEnHtml || null,
      });

      if (response.data.success) {
        setAlert({
          show: true,
          type: "success",
          message: editMode
            ? "Content berhasil diupdate!"
            : "Content berhasil ditambahkan!",
        });

        setTimeout(() => {
          handleCloseModal();
          fetchContents();
        }, 1500);
      } else {
        setAlert({
          show: true,
          type: "warning",
          message:
            response.data.message || "Gagal menyimpan. Silakan coba lagi.",
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        type: "danger",
        message:
          error.response?.data?.message ||
          "Terjadi kesalahan. Pastikan server berjalan dan coba lagi.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus content ini?")) {
      return;
    }

    try {
      await deleteContent(id);
      fetchContents();
      setAlert({
        show: true,
        type: "success",
        message: "Content berhasil dihapus!",
      });
      setTimeout(() => setAlert({ show: false, type: "", message: "" }), 3000);
    } catch (error) {
      setAlert({
        show: true,
        type: "danger",
        message: "Gagal menghapus content.",
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="content-management-page">
      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-file-alt"></i>
            </div>
            <div>
              <h1 className="page-header-title">Kelola Content</h1>
              <p className="page-header-subtitle">
                Manage content untuk website
              </p>
            </div>
          </div>
          <Button
            variant="outline-light"
            className="btn-add"
            onClick={() => handleShowModal()}
          >
            <i className="fas fa-plus me-2"></i>
            Tambah Content
          </Button>
        </div>
      </div>

      {alert.show && (
        <Alert
          variant={alert.type}
          dismissible
          onClose={() => setAlert({ show: false, type: "", message: "" })}
        >
          {alert.message}
        </Alert>
      )}

      <Card className="table-card">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <Table responsive hover className="content-table">
              <thead>
                <tr>
                  <th width="5%">ID</th>
                  <th width="20%">Judul</th>
                  <th width="15%">Kode</th>
                  <th width="30%">Isi (Preview)</th>
                  <th width="13%">Update</th>
                  <th width="8%">User</th>
                  <th width="9%">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {contents.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                      <p className="text-muted">Belum ada content</p>
                    </td>
                  </tr>
                ) : (
                  contents.map((content) => (
                    <tr key={content.id}>
                      <td>{content.id}</td>
                      <td>
                        <strong>
                          {content.judul || (
                            <span className="text-muted">—</span>
                          )}
                        </strong>
                      </td>
                      <td>
                        <span className="badge-jenis">{content.jenis}</span>
                      </td>
                      <td>
                        <div
                          className="content-preview"
                          dangerouslySetInnerHTML={{
                            __html: content.isi?.substring(0, 100) + "...",
                          }}
                        />
                      </td>
                      <td>{formatDate(content.tglupdate)}</td>
                      <td>{content.userid}</td>
                      <td>
                        <div className="action-buttons">
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => handleShowModal(content)}
                            title="Edit"
                          >
                            <i className="fas fa-edit"></i>
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(content.id)}
                            title="Delete"
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal Form */}
      <Modal show={showModal} onHide={handleCloseModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode
              ? `Edit Content: ${getContentLabel(currentContent)}`
              : "Tambah Content Baru"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
          {/* Action buttons di atas agar selalu terlihat */}
          <div className="d-flex gap-2 justify-content-end mb-3 pb-3 border-bottom">
            {alert.show && (
              <Alert
                variant={alert.type}
                className="mb-0 py-2 px-3 flex-grow-1"
                dismissible
                onClose={() => setAlert({ show: false, type: "", message: "" })}
              >
                {alert.message}
              </Alert>
            )}
            <Button variant="secondary" onClick={handleCloseModal}>
              Batal
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="content-form"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Menyimpan...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Simpan
                </>
              )}
            </Button>
          </div>

          <Form id="content-form" onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Kode Konten *</Form.Label>
              <Form.Control
                type="text"
                list="jenis-datalist"
                name="jenis"
                value={currentContent.jenis}
                onChange={handleChange}
                required
                disabled={editMode}
                placeholder="Contoh: profil, visi-misi, sejarah-lembaga"
              />
              <datalist id="jenis-datalist">
                {[...new Set(contents.map((c) => c.jenis).filter(Boolean))].map(
                  (j) => (
                    <option key={j} value={j} />
                  ),
                )}
              </datalist>
              <Form.Text className="text-muted">
                {editMode ? (
                  "Kode konten tidak dapat diubah setelah dibuat."
                ) : (
                  <>
                    Ketik kode unik untuk halaman ini, gunakan huruf kecil dan
                    tanda hubung, tanpa spasi (misal: <code>profil</code>,{" "}
                    <code>visi-misi</code>, <code>sejarah-lembaga</code>).{" "}
                    <strong>
                      Kode ini yang nantinya dipilih di Kelola Menu saat membuat
                      menu bertipe "Konten dari database".
                    </strong>
                  </>
                )}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Judul / Nama Lengkap *</Form.Label>
              <Form.Control
                type="text"
                name="judul"
                value={currentContent.judul}
                onChange={handleChange}
                required
                placeholder="Contoh: Profil Organisasi, Visi Misi dan Tujuan"
              />
              <Form.Text className="text-muted">
                Nama lengkap yang ditampilkan sebagai judul halaman dan
                digunakan sebagai label pilihan di Kelola Menu.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Isi Content (Indonesia) *</Form.Label>
              <ReactQuill
                ref={quillIsiRef}
                theme="snow"
                value={currentContent.isi}
                onChange={handleEditorChange}
                onFocus={(_range, _source, editor) => {
                  activeQuillRef.current = editor;
                }}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Tulis content di sini... Anda bisa copy-paste gambar dari clipboard (Ctrl+V)"
              />
              <Form.Text className="text-muted">
                <strong>Layout foto + teks:</strong> gunakan tombol{" "}
                <em>Foto+Teks</em> untuk menyisipkan foto di kiri dengan teks
                rata kanan di sampingnya. Atau sisipkan gambar lalu klik{" "}
                <em>Foto Kiri</em>/<em>Foto Kanan</em>, pilih teks di
                sampingnya, lalu gunakan tombol rata kanan. Gambar bisa
                di-resize dengan menarik sudutnya.
              </Form.Text>
            </Form.Group>

            {/* ---- English Version ---- */}
            <hr />
            <h6 className="text-muted mb-3">
              <i className="fas fa-language me-2"></i>Versi Bahasa Inggris
              (Opsional)
            </h6>

            <Form.Group className="mb-3">
              <Form.Label>Title (English)</Form.Label>
              <Form.Control
                type="text"
                name="judul_en"
                value={currentContent.judul_en || ""}
                onChange={handleChange}
                placeholder="Enter page title in English"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Content (English)</Form.Label>
              <div className="quill-en">
                <ReactQuill
                  ref={quillIsiEnRef}
                  theme="snow"
                  value={currentContent.isi_en || ""}
                  onChange={handleEditorChangeEn}
                  onFocus={(_range, _source, editor) => {
                    activeQuillRef.current = editor;
                  }}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write content in English here..."
                />
              </div>
            </Form.Group>

            <div className="mt-2" />
          </Form>
        </Modal.Body>
      </Modal>

      {/* Hidden file inputs for toolbar buttons */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
        onChange={handleFileSelected}
      />
      <input
        type="file"
        ref={imageInputRef}
        style={{ display: "none" }}
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleImageSelected}
      />

      {/* HTML Source Editor Modal */}
      <Modal
        show={htmlModal.visible}
        onHide={handleHtmlModalClose}
        size="xl"
        centered
        className="html-source-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontFamily: "Consolas, monospace", fontSize: "1rem" }}>
            <i className="fas fa-code me-2"></i>
            Edit HTML Source
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          <div
            style={{
              background: "#f8fafc",
              padding: "6px 16px",
              fontSize: "11px",
              color: "#64748b",
              fontFamily: "Consolas, monospace",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            Edit HTML secara langsung. Klik{" "}
            <strong style={{ color: "#16a34a" }}>Terapkan</strong> untuk
            menyimpan perubahan ke editor, atau{" "}
            <strong style={{ color: "#dc2626" }}>Batal</strong> untuk membuang
            perubahan.
          </div>
          <textarea
            style={{
              width: "100%",
              height: "65vh",
              background: "#ffffff",
              color: "#1e293b",
              fontFamily: "Consolas, 'Courier New', monospace",
              fontSize: "13px",
              lineHeight: "1.6",
              border: "none",
              padding: "16px",
              resize: "none",
              outline: "none",
              display: "block",
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
              overflowX: "hidden",
            }}
            value={htmlModal.value}
            onChange={(e) =>
              setHtmlModal((prev) => ({ ...prev, value: e.target.value }))
            }
            spellCheck={false}
          />
        </Modal.Body>
        <Modal.Footer style={{ gap: "0.5rem" }}>
          <span style={{ color: "#94a3b8", fontSize: "12px", flex: 1, fontFamily: "Consolas, monospace" }}>
            {htmlModal.value.length} karakter
          </span>
          <Button variant="secondary" onClick={handleHtmlModalClose}>
            Batal
          </Button>
          <Button variant="success" onClick={handleHtmlModalSave}>
            <i className="fas fa-check me-2"></i>Terapkan
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ContentManagement;
